import { Mutex } from 'async-mutex';
import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';

import { HttpStatusCode } from '../assets/enums/index.ts';
import { API } from './api.ts';
import { emitApiErrorPageEvent, shouldDisplayApiErrorPage } from './response-errors.ts';

const mutex = new Mutex();
const REFRESH_TOKEN_URL = '/auth/refresh';

type AuthRequestConfig = InternalAxiosRequestConfig & {
  _retry?: boolean;
  _accessToken?: string | null;
  _accessTokenVersion?: number;
};

let accessTokenVersion = 0;

/**
 * Stores the token pair returned by the refresh endpoint and advances the
 * version used to detect requests that were sent with an expired token.
 */
const storeAuthTokens = (accessToken: string, refreshToken: string) => {
  localStorage.setItem('access_token', accessToken);
  localStorage.setItem('refresh_token', refreshToken);
  accessTokenVersion += 1;
};

/**
 * Clears the session only after the refresh flow has actually failed.
 */
const clearAuthTokens = () => {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  accessTokenVersion += 1;
};

export const instanceCore = axios.create({
  baseURL: '/api/',
  withCredentials: true,
});

instanceCore.interceptors.request.use((config) => {
  const authConfig = config as AuthRequestConfig;
  const token = localStorage.getItem('access_token');

  authConfig._accessToken = token;
  authConfig._accessTokenVersion = accessTokenVersion;

  if (token) {
    config.headers.set('Authorization', `Bearer ${token}`);
  }

  return config;
});

instanceCore.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (!error.config?.errorHandledLocally && shouldDisplayApiErrorPage(error)) {
      emitApiErrorPageEvent(error);
    }

    return Promise.reject(error);
  }
);

instanceCore.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as AuthRequestConfig | undefined;

    // Only refresh an original API request. A failed refresh request must
    // propagate so the session can be cleared and the user can log in again.
    if (
      error.response?.status === HttpStatusCode.UNAUTHORIZED_401 &&
      originalRequest &&
      !originalRequest._retry &&
      originalRequest.url !== REFRESH_TOKEN_URL
    ) {
      const currentAccessToken = localStorage.getItem('access_token');
      const requestWasSentWithAccessToken = originalRequest._accessToken;
      const requestAccessTokenVersion = originalRequest._accessTokenVersion;

      // This request received 401 for an old token while another request
      // already completed the refresh. Retry it without starting another
      // refresh, even if the mutex has already been released.
      const hasNewerAccessToken =
        Boolean(currentAccessToken) &&
        (requestWasSentWithAccessToken !== currentAccessToken ||
          requestAccessTokenVersion !== accessTokenVersion);

      if (hasNewerAccessToken) {
        originalRequest._retry = true;
        return instanceCore(originalRequest);
      }

      // Another request is refreshing the token. Wait for that exact
      // refresh and retry only if it produced a new access token.
      if (mutex.isLocked()) {
        await mutex.waitForUnlock();

        const refreshedAccessToken = localStorage.getItem('access_token');
        const hasRefreshSucceeded =
          Boolean(refreshedAccessToken) &&
          (requestWasSentWithAccessToken !== refreshedAccessToken ||
            requestAccessTokenVersion !== accessTokenVersion);

        if (hasRefreshSucceeded) {
          originalRequest._retry = true;
          return instanceCore(originalRequest);
        }

        return Promise.reject(error);
      }

      const release = await mutex.acquire();
      try {
        // The token could have changed between the initial check and
        // acquiring the mutex. Avoid a redundant refresh in that case.
        const latestAccessToken = localStorage.getItem('access_token');
        const hasRefreshCompleted =
          Boolean(latestAccessToken) &&
          (requestWasSentWithAccessToken !== latestAccessToken ||
            requestAccessTokenVersion !== accessTokenVersion);

        if (hasRefreshCompleted) {
          originalRequest._retry = true;
          return instanceCore(originalRequest);
        }

        const refreshToken = localStorage.getItem('refresh_token');
        if (!refreshToken) {
          clearAuthTokens();
          return Promise.reject(error);
        }

        const { access_token, refresh_token } = await API.auth.refresh(refreshToken);

        if (!access_token || !refresh_token) {
          clearAuthTokens();
          return Promise.reject(error);
        }

        storeAuthTokens(access_token, refresh_token);
        originalRequest._retry = true;
        return instanceCore(originalRequest);
      } catch (refreshError) {
        clearAuthTokens();
        return Promise.reject(refreshError);
      } finally {
        release();
      }
    }

    return Promise.reject(error);
  }
);
